import { Link } from "react-router-dom";
import { Package, ShieldCheck, XCircle, Truck, FileText } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { STORE_ADDRESS, STORE_PHONE, PALLET } from "@/constants/store";

export default function PalletPolicyPage() {
  usePageMeta("Pallet Guarantee & Terms",
    "Pallet Guarantee, delivery/pickup terms, resale tax exemption (Form 3372), and manifest disclaimer for IPVALUE MARKETPLACE closeout & overstock pallets — serving Grand Rapids and West Michigan from Grand Rapids, MI.");
  return (
    <div className="max-w-4xl mx-auto px-6 md:px-8 py-12" data-testid="policy-pallet-page">
      <div className="ipv-eyebrow text-[#FFC220]">IPVALUE MARKETPLACE</div>
      <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2 text-[#0A1C40]">
        Pallet Guarantee &amp; Terms
      </h1>
      <p className="mt-4 text-lg text-gray-700 leading-relaxed max-w-2xl">
        Everything you need to know before you buy a closeout or overstock pallet
        from us — the guarantee, the manifest, the delivery rules, and the
        resale requirements.
      </p>

      {/* The guarantee — headline */}
      <section className="mt-10 bg-[#0A1C40] text-white p-8" data-testid="policy-pallet-guarantee">
        <div className="flex items-center gap-2 text-[#FFC220]">
          <ShieldCheck className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">The Pallet Guarantee — Store Credit Only</div>
        </div>
        <h2 className="mt-4 font-display font-black text-3xl lg:text-4xl tracking-tight text-[#FFC220]">
          Broken, dead, or missing?<br />
          <span className="text-white">We'll credit you back.</span>
        </h2>
        <ul className="mt-6 space-y-2 text-[15px]">
          {PALLET.guarantee.map((line) => (
            <li key={line} className="flex gap-3">
              <ShieldCheck className="w-4 h-4 text-[#FFC220] shrink-0 mt-0.5" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* What's NOT covered */}
      <section className="mt-10 border-l-4 border-[#DC2626] bg-red-50 p-6" data-testid="policy-pallet-exclusions">
        <div className="flex items-center gap-2 text-[#DC2626]">
          <XCircle className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">What the Guarantee Does NOT Cover</div>
        </div>
        <ul className="mt-3 ml-6 list-disc text-sm text-gray-800 space-y-1 leading-relaxed">
          {PALLET.guaranteeExclusions.map((line) => <li key={line}>{line}</li>)}
        </ul>
      </section>

      {/* Manifest disclaimer */}
      <section className="mt-10 border-l-4 border-[#FFC220] bg-[#FFC220]/10 p-6" data-testid="policy-pallet-manifest">
        <div className="flex items-center gap-2 text-[#8a6a00]">
          <FileText className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">About the Manifest</div>
        </div>
        <div className="mt-3 text-sm text-gray-800 space-y-2 leading-relaxed">
          <p>
            Every pallet listing has its manifest attached as a downloadable PDF
            or displayed as an on-page table.
          </p>
          <p>
            <b>The manifest is an estimate.</b> Quantities, brands, and condition
            can vary. The manifested retail value is what the goods once sold for
            — <b>not</b> what they are worth today.
          </p>
        </div>
      </section>

      {/* Delivery & pickup */}
      <section className="mt-10" data-testid="policy-pallet-delivery">
        <div className="flex items-center gap-2 text-[#0A1C40]">
          <Truck className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">Pallet Delivery &amp; Pickup</div>
        </div>
        <div className="mt-4 border-2 border-[#0A1C40] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0A1C40] text-white text-left">
              <tr>
                <th className="p-3 font-black uppercase tracking-wider text-xs">Distance</th>
                <th className="p-3 font-black uppercase tracking-wider text-xs text-right">Fee</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr><td className="p-3">Within 10 miles</td><td className="p-3 text-right font-black">${PALLET.deliveryFlat10mi}.00</td></tr>
              <tr><td className="p-3">Within 25 miles</td><td className="p-3 text-right font-black">${PALLET.deliveryFlat25mi}.00</td></tr>
              <tr><td className="p-3">Beyond 25 miles</td><td className="p-3 text-right text-[#0A1C40] font-bold">Request a quote</td></tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-sm text-gray-800 space-y-2 leading-relaxed">
          <p><b>Curbside or driveway only</b> — we do not carry pallets inside.</p>
          <p><b>Pickup is by appointment.</b> Bring a truck, trailer, or van. We load; you strap and secure.</p>
          <p>Pick up within <b>48 hours of payment</b>; storage is <b>$25/day</b> after that. Pallets left more than <b>10 days</b> after payment are forfeited.</p>
          <p className="text-[#DC2626] font-bold">A pallet never qualifies for the $299 free-delivery threshold.</p>
        </div>
      </section>

      {/* Selling terms */}
      <section className="mt-10 border-l-4 border-gray-400 bg-gray-50 p-6" data-testid="policy-pallet-sale-terms">
        <div className="ipv-eyebrow font-black text-[#0A1C40]">Selling Terms</div>
        <div className="mt-3 text-sm text-gray-800 space-y-2 leading-relaxed">
          <p><b>Pallets sell whole.</b> No cherry-picking, no swapping items off a pallet.</p>
          <p><b>Sales tax:</b> 6% applies.</p>
          <p>
            <b>Resale buyers</b> must have Michigan <b>Form 3372</b> on file BEFORE
            purchase to buy tax-exempt.
          </p>
        </div>
      </section>

      {/* Claim process */}
      <section className="mt-10 border-l-4 border-[#0A1C40] bg-[#EEF3FF] p-6" data-testid="policy-pallet-claim">
        <div className="ipv-eyebrow font-black text-[#0A1C40]">How to File a Claim</div>
        <ol className="mt-3 ml-6 list-decimal text-sm text-gray-800 space-y-1 leading-relaxed">
          <li>Inventory your pallet <b>within 48 hours</b> of pickup or delivery.</li>
          <li>Photograph each broken, non-functional, or missing item.</li>
          <li>Submit <b>one claim per pallet, all items at once</b>. Email the photos with your order number to us or bring them in.</li>
          <li>Approved claims are credited as store credit, valid <b>90 days</b>.</li>
        </ol>
      </section>

      <div className="mt-10 text-sm text-gray-600">
        Questions? Come see us at <b>{STORE_ADDRESS.short}</b> or call <a className="underline font-bold text-[#0A1C40]" href={`tel:+1${STORE_PHONE.replace(/\D/g, "")}`}>{STORE_PHONE}</a>.
      </div>

      <div className="mt-8 text-center flex justify-center gap-3 flex-wrap">
        <Link to="/shop/Closeout & Overstock Pallets" className="btn-gold inline-flex items-center gap-2" data-testid="policy-shop-pallets-btn">
          <Package className="w-4 h-4" /> Shop Pallets
        </Link>
      </div>
    </div>
  );
}
